import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { X, Blend, Check } from 'lucide-react';
import type { AppToast, RoomToast, AchievementToast, ChallengeToast, RematchToast, InfoToast } from '../context/NotificationContext.tsx';
import Avatar from './Avatar.tsx';
import { getAchievementIcon } from '../lib/achievementIcons.tsx';
import { getExistingSocket } from '../multiplayer/socket.ts';
import { C2S } from '../multiplayer/events.ts';

const TOAST_DURATION_MS = 8000;
/** Challenge toasts live as long as the server keeps the invite open (~30s). */
const CHALLENGE_DURATION_MS = 30000;

function useAutoDismiss(id: string, onDismiss: (id: string) => void, durationMs: number = TOAST_DURATION_MS) {
  useEffect(() => {
    const timer = setTimeout(() => onDismiss(id), durationMs);
    return () => clearTimeout(timer);
  }, [id, onDismiss, durationMs]);
}

// ── Room toast ──────────────────────────────────────────────────────────────

function RoomToastItem({
  toast,
  onDismiss,
}: {
  toast: RoomToast;
  onDismiss: (id: string) => void;
}) {
  const navigate = useNavigate();
  useAutoDismiss(toast.id, onDismiss);

  const handleJoin = () => {
    navigate('/multiplayer');
    onDismiss(toast.id);
  };

  return (
    <div className="room-toast animate-toast-in">
      {/* Icon */}
      <div style={{
        width: 36, height: 36, borderRadius: 'var(--radius-card)', flexShrink: 0,
        background: 'color-mix(in oklab, var(--accent) 14%, transparent)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--accent)',
      }}>
        <Blend size={18} strokeWidth={1.6} />
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600,
          color: 'var(--ink)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          New multiplayer room: {toast.roomName}
        </div>
        <div style={{
          fontFamily: 'var(--font-ui)', fontSize: 11, color: 'var(--muted)',
          marginTop: 1,
        }}>
          {toast.hostUsername} · {toast.gridSize}×{toast.gridSize} · {toast.format} · {toast.playerCount}/{toast.maxPlayers}
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
        <button
          onClick={handleJoin}
          className="btn btn-accent btn-sm"
          style={{ whiteSpace: 'nowrap' }}
        >
          Join
        </button>
        <button
          onClick={() => onDismiss(toast.id)}
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'var(--muted)', padding: 4, borderRadius: 6,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            minWidth: 28, minHeight: 28,
          }}
          aria-label="Dismiss"
        >
          <X size={14} />
        </button>
      </div>

      {/* Auto-dismiss progress bar */}
      <div className="room-toast-progress" />
    </div>
  );
}

// ── Achievement toast ─────────────────────────────────────────────────────────

function AchievementToastItem({
  toast,
  onDismiss,
}: {
  toast: AchievementToast;
  onDismiss: (id: string) => void;
}) {
  const navigate = useNavigate();
  useAutoDismiss(toast.id, onDismiss);

  const handleView = () => {
    navigate('/achievements');
    onDismiss(toast.id);
  };

  return (
    <div className="room-toast animate-toast-in" onClick={handleView} style={{ cursor: 'pointer' }}>
      {/* Icon — achievement-specific icon */}
      <div style={{
        width: 36, height: 36, borderRadius: 'var(--radius-card)', flexShrink: 0,
        background: 'color-mix(in oklab, var(--accent) 14%, transparent)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--accent)', overflow: 'hidden',
      }}>
        {getAchievementIcon(toast.achievementId, 18)}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600,
          color: 'var(--ink)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          Achievement: {toast.name}
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
        <button
          onClick={(e) => { e.stopPropagation(); handleView(); }}
          className="btn btn-accent btn-sm"
          style={{ whiteSpace: 'nowrap' }}
        >
          See
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDismiss(toast.id); }}
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'var(--muted)', padding: 4, borderRadius: 6,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            minWidth: 28, minHeight: 28,
          }}
          aria-label="Dismiss"
        >
          <X size={14} />
        </button>
      </div>

      {/* Auto-dismiss progress bar */}
      <div className="room-toast-progress" />
    </div>
  );
}

// ── Challenge toast ───────────────────────────────────────────────────────────

function ChallengeToastItem({
  toast,
  onDismiss,
}: {
  toast: ChallengeToast;
  onDismiss: (id: string) => void;
}) {
  useAutoDismiss(toast.id, onDismiss, CHALLENGE_DURATION_MS);

  const handleAccept = () => {
    // Navigation is driven by the challenge:accepted event (see NotificationContext),
    // which carries the room id — that lets us go straight to the room instead of
    // bouncing through the lobby and possibly missing the room:state on remount.
    getExistingSocket()?.emit(C2S.CHALLENGE_ACCEPT, { challengeId: toast.challengeId });
    onDismiss(toast.id);
  };

  const handleDecline = () => {
    getExistingSocket()?.emit(C2S.CHALLENGE_DECLINE, { challengeId: toast.challengeId });
    onDismiss(toast.id);
  };

  return (
    <div className="room-toast animate-toast-in">
      {/* Challenger avatar */}
      <div style={{ flexShrink: 0 }}>
        <Avatar username={toast.fromUsername} avatarFilename={toast.fromAvatar} size={36} />
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600,
          color: 'var(--ink)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {toast.fromUsername} challenges you!
        </div>
        <div style={{
          fontFamily: 'var(--font-ui)', fontSize: 11, color: 'var(--muted)',
          marginTop: 1,
        }}>
          {toast.config.gridSize}×{toast.config.gridSize} · {toast.config.format} · 1v1
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        <button
          onClick={handleAccept}
          aria-label="Accept"
          style={{
            background: 'color-mix(in oklab, var(--robot-green) 16%, transparent)',
            color: 'var(--robot-green)',
            border: 'none', borderRadius: 'var(--radius-pill)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 30, height: 30, cursor: 'pointer',
          }}
        >
          <Check size={16} strokeWidth={2.4} />
        </button>
        <button
          onClick={handleDecline}
          aria-label="Decline"
          style={{
            background: 'color-mix(in oklab, var(--robot-red) 16%, transparent)',
            color: 'var(--robot-red)',
            border: 'none', borderRadius: 'var(--radius-pill)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 30, height: 30, cursor: 'pointer',
          }}
        >
          <X size={16} strokeWidth={2.4} />
        </button>
      </div>

      {/* Expiry time bar — drains over the invite's lifetime */}
      <div className="room-toast-progress" style={{ animationDuration: `${CHALLENGE_DURATION_MS}ms` }} />
    </div>
  );
}

// ── Rematch toast ─────────────────────────────────────────────────────────────

function RematchToastItem({
  toast,
  onDismiss,
}: {
  toast: RematchToast;
  onDismiss: (id: string) => void;
}) {
  const navigate = useNavigate();
  useAutoDismiss(toast.id, onDismiss);

  const handleJoin = () => {
    // Emitting GAME_REMATCH adds us to the rematch room server-side; navigating
    // mounts LiveGameScreen, whose rejoin() re-emits room:state so we land on the
    // waiting screen even if the first room:state arrived before we subscribed.
    getExistingSocket()?.emit(C2S.GAME_REMATCH, {
      originalGameId: toast.originalGameId,
      config: toast.config,
    });
    navigate(`/multiplayer/${toast.roomId}`);
    onDismiss(toast.id);
  };

  return (
    <div className="room-toast animate-toast-in">
      {/* Initiator avatar */}
      <div style={{ flexShrink: 0 }}>
        <Avatar username={toast.fromUsername} avatarFilename={toast.fromAvatar} size={36} />
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600,
          color: 'var(--ink)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {toast.fromUsername} wants a rematch!
        </div>
        <div style={{
          fontFamily: 'var(--font-ui)', fontSize: 11, color: 'var(--muted)',
          marginTop: 1,
        }}>
          {toast.config.gridSize}×{toast.config.gridSize} · {toast.config.format}
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
        <button
          onClick={handleJoin}
          className="btn btn-accent btn-sm"
          style={{ whiteSpace: 'nowrap' }}
        >
          Join
        </button>
        <button
          onClick={() => onDismiss(toast.id)}
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'var(--muted)', padding: 4, borderRadius: 6,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            minWidth: 28, minHeight: 28,
          }}
          aria-label="Dismiss"
        >
          <X size={14} />
        </button>
      </div>

      {/* Auto-dismiss progress bar */}
      <div className="room-toast-progress" />
    </div>
  );
}

// ── Info toast ────────────────────────────────────────────────────────────────

function InfoToastItem({
  toast,
  onDismiss,
}: {
  toast: InfoToast;
  onDismiss: (id: string) => void;
}) {
  useAutoDismiss(toast.id, onDismiss);

  return (
    <div className="room-toast animate-toast-in">
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 500,
          color: 'var(--ink)',
        }}>
          {toast.message}
        </div>
      </div>
      <button
        onClick={() => onDismiss(toast.id)}
        style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: 'var(--muted)', padding: 4, borderRadius: 6,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          minWidth: 28, minHeight: 28, flexShrink: 0,
        }}
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>

      {/* Auto-dismiss progress bar (drains over 8s) */}
      <div className="room-toast-progress" />
    </div>
  );
}

function ToastItem({ toast, onDismiss }: { toast: AppToast; onDismiss: (id: string) => void }) {
  if (toast.kind === 'achievement') return <AchievementToastItem toast={toast} onDismiss={onDismiss} />;
  if (toast.kind === 'challenge') return <ChallengeToastItem toast={toast} onDismiss={onDismiss} />;
  if (toast.kind === 'rematch') return <RematchToastItem toast={toast} onDismiss={onDismiss} />;
  if (toast.kind === 'info') return <InfoToastItem toast={toast} onDismiss={onDismiss} />;
  return <RoomToastItem toast={toast} onDismiss={onDismiss} />;
}

// ── Container (portal) ────────────────────────────────────────────────────────

export function ToastContainer({
  toasts,
  onDismiss,
}: {
  toasts: AppToast[];
  onDismiss: (id: string) => void;
}) {
  if (toasts.length === 0) return null;

  return createPortal(
    <div className="toast-container" aria-live="polite" aria-label="Notifications">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>,
    document.body
  );
}
